import React from 'react';
import { Box, TableRow, TableCell, Typography, IconButton, Tooltip, Avatar } from '@mui/material';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import NotesIcon from '@mui/icons-material/Notes';
import LayersIcon from '@mui/icons-material/Layers';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import BoyIcon from '@mui/icons-material/Boy';
import { getCapturedImageDisplayUrl } from '../../../services/s3/captureUrlService';
import { getInterestLevelColor, shouldInterestLevelPulse } from '../../events/utils/interestLevel';

/**
 * MultiDatasetMatchEventRow - Renders a multi-dataset match event in aggregator table
 *
 * Shows when the same person is matched across multiple datasets
 */
const MultiDatasetMatchEventRow = React.memo(function MultiDatasetMatchEventRow({
  event,
  isChild = false,
  address,
  onLocationClick
}) {
  const hasValidMetadata = event.metadata &&
    typeof event.metadata === 'object' &&
    !event.metadata.S &&
    !event.metadata.M &&
    !event.metadata.NULL;

  if (!hasValidMetadata || !event.metadata?.personName) {
    return null;
  }

  const capturedImageUrl = getCapturedImageDisplayUrl(event.metadata.capturedImageUrl);
  const noteCount = event.metadata?.notes?.length || 0;
  const timestamp = new Date(event.timestamp || event.createdAt);
  const personName = event.metadata.personName;
  const personImages = event.metadata?.personImages || [];
  const datasets = event.metadata?.datasets || [];
  const datasetCount = event.metadata?.datasetCount || datasets.length;
  const persons = event.metadata?.persons || [];
  const primaryPerson = persons[0];
  // Unified schema: read from interest object
  const interestLevel = primaryPerson?.interest?.level;
  const interestReason = primaryPerson?.interest?.reason;
  const interestColor = getInterestLevelColor(interestLevel);
  const shouldPulse = shouldInterestLevelPulse(interestLevel);
  const sightingCount = event.metadata?.sightingCount !== undefined ? event.metadata.sightingCount : null;
  const matchAccuracy = event.metadata?.matchAccuracy;
  const matchSource = event.metadata?.matchSource;

  // Get color for processing elapsed time
  const getElapsedTimeColor = (ms) => {
    if (ms < 500) return '#4caf50'; // Green
    if (ms < 750) return '#ff9800'; // Amber
    if (ms >= 1500) return '#f44336'; // Red
    return '#ff9800'; // Amber for 750-1499ms range
  };

  // Determine background color based on relationship
  const backgroundColor = isChild ? '#f5f5f5' : '#f3e5f5'; // Light purple for multi-dataset
  const borderColor = isChild ? '4px solid #ffcc80' : '6px solid rgba(142, 36, 170, 0.4)'; // Purple border

  return (
    <TableRow
      hover
      sx={{
        backgroundColor,
        borderLeft: borderColor,
        '& .MuiTableCell-root': { borderBottom: '1px solid rgba(224, 224, 224, 0.4)' },
        '&:hover': {
          backgroundColor,
          filter: 'brightness(0.98)'
        }
      }}
    >
      {/* Event Type Icon */}
      <TableCell align="center" sx={{ width: 40 }}>
        <Tooltip title={`Multi-Dataset Match (${datasetCount} datasets)`}>
          <Box sx={{ position: 'relative', display: 'inline-flex', alignItems: 'center' }}>
            <LayersIcon sx={{ color: 'secondary.main', fontSize: 28 }} />
            <Box
              sx={{
                position: 'absolute',
                top: -4,
                right: -8,
                backgroundColor: 'secondary.main',
                color: 'white',
                borderRadius: '50%',
                width: 16,
                height: 16,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: 10,
                fontWeight: 'bold'
              }}
            >
              {datasetCount}
            </Box>
          </Box>
        </Tooltip>
      </TableCell>

      {/* Expand/Collapse Column (empty for multi-dataset matches) */}
      <TableCell align="center" sx={{ width: 40 }} />

      {/* Event Details */}
      <TableCell>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%' }}>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
              {/* Match confidence indicator - AI match */}
              {matchSource === 'face_match' && matchAccuracy && (
                <Box sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  gap: 0.25,
                  flexShrink: 0
                }}>
                  {/* AI match icon */}
                  <AutoAwesomeIcon sx={{ color: 'primary.main', fontSize: 20 }} />

                  {/* Accuracy circle */}
                  <Box sx={{
                    position: 'relative',
                    width: 28,
                    height: 28,
                    borderRadius: '50%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    background: `conic-gradient(${
                      matchAccuracy >= 1.0 ? '#2e7d32' :
                      matchAccuracy >= 0.9 ? '#2e7d32' :
                      matchAccuracy >= 0.8 ? '#c77700' :
                      '#b71c1c'
                    } ${matchAccuracy * 360}deg, rgba(0, 0, 0, 0.1) 0deg)`
                  }}>
                    <Box sx={{
                      width: 23,
                      height: 23,
                      borderRadius: '50%',
                      backgroundColor: 'white',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}>
                      <Typography
                        variant="caption"
                        sx={{
                          fontWeight: 700,
                          fontSize: '0.5rem',
                          color:
                            matchAccuracy >= 1.0 ? '#2e7d32' :
                            matchAccuracy >= 0.9 ? '#2e7d32' :
                            matchAccuracy >= 0.8 ? '#c77700' :
                            '#b71c1c',
                          lineHeight: 1
                        }}
                      >
                        {matchAccuracy >= 1.0
                          ? '100'
                          : `${(matchAccuracy * 100).toFixed(0)}`
                        }
                      </Typography>
                    </Box>
                  </Box>
                </Box>
              )}

              {/* Manual match indicator */}
              {(matchSource === 'human_match' || matchSource === 'manual_filter') && (
                <Box sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  gap: 0.25,
                  flexShrink: 0
                }}>
                  {/* Manual match icon */}
                  <BoyIcon sx={{ color: matchSource === 'manual_filter' ? '#c77700' : '#2e7d32', fontSize: 20 }} />

                  {/* Manual indicator circle */}
                  <Box sx={{
                    position: 'relative',
                    width: 28,
                    height: 28,
                    borderRadius: '50%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    background: `conic-gradient(${matchSource === 'manual_filter' ? '#c77700' : '#2e7d32'} 360deg, rgba(0, 0, 0, 0.1) 0deg)`
                  }}>
                    <Box sx={{
                      width: 23,
                      height: 23,
                      borderRadius: '50%',
                      backgroundColor: 'white',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}>
                      <Typography
                        variant="caption"
                        sx={{
                          fontWeight: 700,
                          fontSize: '0.65rem',
                          color: matchSource === 'manual_filter' ? '#c77700' : '#2e7d32',
                          lineHeight: 1
                        }}
                      >
                        ?
                      </Typography>
                    </Box>
                  </Box>
                </Box>
              )}

              {/* Multi-image avatar - reference images from datasets */}
              <Tooltip title={interestReason || (interestLevel ? `Interest: ${interestLevel}` : '')} arrow placement="right" disableHoverListener={!interestLevel}>
                <Box
                  onClick={() => {
                    const personUri = primaryPerson?.uri;
                    if (personUri) {
                      window.open(personUri, '_blank', 'noreferrer');
                    }
                  }}
                  sx={{
                    cursor: primaryPerson?.uri ? 'pointer' : 'default',
                    '&:hover': primaryPerson?.uri ? { opacity: 0.8 } : {},
                    border: interestLevel ? `3px solid ${interestColor}` : 'none',
                    borderRadius: '50%',
                    padding: interestLevel ? '2px' : 0,
                    display: 'inline-flex',
                    flexShrink: 0,
                    animation: shouldPulse ? 'pulse 1.5s ease-in-out infinite' : 'none',
                    '@keyframes pulse': {
                      '0%, 100%': {
                        boxShadow: `0 0 0 0 ${interestColor}99`,
                        transform: 'scale(1)'
                      },
                      '50%': {
                        boxShadow: `0 0 0 12px ${interestColor}00`,
                        transform: 'scale(1.05)'
                      }
                    }
                  }}
                >
                  {personImages.length === 1 ? (
                    <Avatar src={personImages[0].imageUrl} alt={personName} sx={{ width: 60, height: 60 }} />
                  ) : personImages.length === 2 ? (
                    <Box sx={{ position: 'relative', width: 60, height: 60 }}>
                      <Box component="img" src={personImages[0].imageUrl} alt={personImages[0].dataset}
                        sx={{ position: 'absolute', left: 0, top: 0, width: 30, height: 60, objectFit: 'cover', borderRadius: '30px 0 0 30px' }} />
                      <Box component="img" src={personImages[1].imageUrl} alt={personImages[1].dataset}
                        sx={{ position: 'absolute', right: 0, top: 0, width: 30, height: 60, objectFit: 'cover', borderRadius: '0 30px 30px 0' }} />
                    </Box>
                  ) : (
                    <Box sx={{ position: 'relative', width: 60, height: 60, borderRadius: '50%', overflow: 'hidden' }}>
                      {personImages.slice(0, 4).map((img, idx) => {
                        const positions = [
                          { top: 0, left: 0, borderRadius: '100% 0 0 0' },
                          { top: 0, right: 0, borderRadius: '0 100% 0 0' },
                          { bottom: 0, left: 0, borderRadius: '0 0 0 100%' },
                          { bottom: 0, right: 0, borderRadius: '0 0 100% 0' }
                        ];
                        return (
                          <Box key={idx} component="img" src={img.imageUrl} alt={img.dataset}
                            sx={{ position: 'absolute', width: 30, height: 30, objectFit: 'cover', ...positions[idx] }} />
                        );
                      })}
                    </Box>
                  )}
                </Box>
              </Tooltip>

              {/* Captured image thumbnail - SECOND (the photo that was taken) */}
              {capturedImageUrl && (
                <Box
                  component="img"
                  src={capturedImageUrl}
                  alt="Captured"
                  onClick={() => window.open(capturedImageUrl, '_blank', 'noreferrer')}
                  sx={{
                    width: 60,
                    height: 60,
                    borderRadius: 1,
                    objectFit: 'cover',
                    border: '2px solid rgba(25, 118, 210, 0.5)',
                    cursor: 'pointer',
                    flexShrink: 0,
                    transition: 'transform 0.2s, box-shadow 0.2s',
                    '&:hover': {
                      transform: 'scale(1.05)',
                      boxShadow: 2
                    }
                  }}
                />
              )}

              {/* Person details - THIRD */}
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                  <Typography variant="h6" sx={{ lineHeight: 1.2 }}>
                    {personName}
                  </Typography>
                  <Tooltip title={`Datasets: ${datasets.join(', ')}`}>
                    <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500 }}>
                      ({datasetCount} datasets)
                    </Typography>
                  </Tooltip>
                </Box>
                <Typography variant="body2" color="text.secondary">
                  {event.userHandle || event.userEmail || event.userId}
                </Typography>
              </Box>
            </Box>
          </Box>

          {noteCount > 0 && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexShrink: 0 }}>
              <NotesIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
              <Typography variant="caption" color="text.secondary">
                {noteCount}
              </Typography>
            </Box>
          )}
        </Box>
      </TableCell>

      {/* Timestamp */}
      <TableCell sx={{ width: 140 }}>
        <Typography variant="caption">
          {timestamp.toLocaleString()}
        </Typography>
        {event.metadata?.processingElapsedMs !== undefined && (
          <Typography
            variant="caption"
            sx={{
              display: 'block',
              fontSize: '0.7rem',
              color: getElapsedTimeColor(event.metadata.processingElapsedMs),
              fontWeight: 500
            }}
          >
            +{event.metadata.processingElapsedMs}ms
          </Typography>
        )}
      </TableCell>

      {/* User Account */}
      <TableCell sx={{ width: 120 }}>
        <Typography variant="caption" color="text.secondary">
          {event.userHandle || event.userEmail || event.userId || '—'}
        </Typography>
      </TableCell>

      {/* Location */}
      <TableCell>
        {event.location ? (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <IconButton
              size="small"
              onClick={(e) => {
                e.stopPropagation();
                onLocationClick(event);
              }}
              sx={{ p: 0.5 }}
            >
              <LocationOnIcon fontSize="small" color="primary" />
            </IconButton>
            <Typography variant="caption">
              {address || `${event.location.latitude.toFixed(4)}, ${event.location.longitude.toFixed(4)}`}
            </Typography>
          </Box>
        ) : (
          <Typography variant="caption">N/A</Typography>
        )}
      </TableCell>
    </TableRow>
  );
}, (prevProps, nextProps) => {
  // Custom comparison function for React.memo
  return (
    prevProps.event.eventId === nextProps.event.eventId &&
    prevProps.event.metadata?.notes?.length === nextProps.event.metadata?.notes?.length &&
    prevProps.isChild === nextProps.isChild &&
    prevProps.address === nextProps.address
  );
});

export default MultiDatasetMatchEventRow;
